/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { forwardRef } from 'react';
import * as React from 'react';

import { Hidden, ProductCard, Skeleton, breakpoints, typography2 } from '@coursera/cds-core';

const IMAGE_DIMENSIONS = {
  MAX_WIDTH: 305,
  MAX_HEIGHT: 125,
} as const;

const styles = {
  wrapper: css`
    display: grid;
    width: 100%;
    max-width: 100%;
  `,
  container: css`
    cursor: pointer;
  `,
  bodyContainer: css`
    display: flex;
    flex-direction: column;
    height: 100%;

    h3,
    strong {
      color: var(--cds-color-neutral-primary);
      font-weight: 600;
    }
  `,
  roleStatsContainer: css`
    margin-top: auto;

    ${breakpoints.down('xs')} {
      p {
        ${typography2.bodyTertiary}
      }
    }
  `,
  productCard: css`
    border-radius: var(--cds-border-radius-100) !important;

    .cds-ProductCard-content {
      gap: var(--cds-spacing-100) !important;
      padding-right: var(--cds-spacing-300) !important;
    }

    .cds-CommonCard-previewImage {
      margin-bottom: 0 !important;
      aspect-ratio: auto !important;
    }

    ${breakpoints.down('md')} {
      max-width: 321px !important;
      min-width: unset !important;
    }

    ${breakpoints.down('xs')} {
      .cds-ProductCard-gridCard {
        padding: 0 !important;
      }

      .cds-CommonCard-previewImage {
        border-radius: var(--cds-border-radius-100) var(--cds-border-radius-100) 0 0 !important;
      }

      .cds-CommonCard-title {
        margin-bottom: var(--cds-spacing-100);
      }

      .cds-ProductCard-content {
        padding-right: var(--cds-spacing-100) !important;
      }
    }
  `,
  loading: {
    title: css`
      margin-top: calc(var(--cds-spacing-100) * -1);
    `,
    description: css`
      margin-top: var(--cds-spacing-150);

      > span {
        margin-bottom: calc(var(--cds-spacing-50) / 2);
      }
    `,
    salary: css`
      margin-top: var(--cds-spacing-200);
    `,
    jobsAvailable: css`
      margin-top: calc(var(--cds-spacing-150) / 2);
    `,
    mobile: {
      title: css`
        margin-top: calc(var(--cds-spacing-100) * -1);
      `,
      salary: css`
        margin-top: calc(var(--cds-spacing-200) + var(--cds-spacing-50));
      `,
    },
  },
};

type CompactCareerRoleCardPlaceholderProps = {
  ref?: React.Ref<HTMLDivElement>;
};

/**
 * @description CompactCareerRoleCardPlaceholder is a placeholder component for the CompactCareerRoleCard.
 * It displays loading states for each card field for both mobile and desktop variants.
 */
const CompactCareerRoleCardPlaceholder = forwardRef<HTMLDivElement, CompactCareerRoleCardPlaceholderProps>((_, ref) => {
  const loadingPreviewImage = ({ isMobile = false }: { isMobile?: boolean }) => (
    <Skeleton variant="rect" height={isMobile ? 80 : IMAGE_DIMENSIONS.MAX_HEIGHT} width="100%" />
  );

  const loadingTitle = ({ isMobile = false }: { isMobile?: boolean }) => (
    <Skeleton
      variant="rect"
      height={20}
      width="85%"
      css={isMobile ? styles.loading.mobile.title : styles.loading.title}
    />
  );

  const loadingDescription = () => (
    <div css={styles.loading.description}>
      <Skeleton variant="rect" height={14} width="95%" />
      <Skeleton variant="rect" height={14} width="80%" />
    </div>
  );

  const loadingSalary = ({ isMobile = false }: { isMobile?: boolean }) => (
    <Skeleton
      variant="rect"
      height={14}
      width="70%"
      css={isMobile ? styles.loading.mobile.salary : styles.loading.salary}
    />
  );

  const loadingJobsAvailable = () => (
    <div css={styles.loading.jobsAvailable}>
      <Skeleton variant="rect" height={14} width="65%" />
    </div>
  );

  const renderMobileBody = () => (
    <div css={styles.bodyContainer}>
      {loadingTitle({ isMobile: true })}
      <div css={styles.roleStatsContainer}>{loadingSalary({ isMobile: true })}</div>
    </div>
  );

  const renderDesktopBody = () => (
    <div css={styles.bodyContainer}>
      {loadingTitle({ isMobile: false })}
      {loadingDescription()}
      <div css={styles.roleStatsContainer}>
        {loadingSalary({ isMobile: false })}
        {loadingJobsAvailable()}
      </div>
    </div>
  );

  const commonProductCardProps: React.ComponentProps<typeof ProductCard> = {
    ref,
    productType: 'role',
    variant: 'grid',
    css: styles.productCard,
  };

  return (
    <div css={styles.wrapper}>
      {/* Mobile variant */}
      <Hidden smUp css={styles.container}>
        <ProductCard
          {...commonProductCardProps}
          renderPreviewImage={() => loadingPreviewImage({ isMobile: true })}
          body={renderMobileBody()}
        />
      </Hidden>

      {/* Desktop variant */}
      <Hidden only="xs" css={styles.container}>
        <ProductCard
          {...commonProductCardProps}
          renderPreviewImage={() => loadingPreviewImage({ isMobile: false })}
          body={renderDesktopBody()}
        />
      </Hidden>
    </div>
  );
});

export default CompactCareerRoleCardPlaceholder;
